import { useMutation } from '@tanstack/react-query';
import { useNotification } from '@wix/gg-components';
import { endpoints } from 'api/endpoints';
import { Category } from 'config/enums';
import { useCallback } from 'react';
import { useProductStore } from 'stores/product';
import { useAppStore } from 'stores/state';

type Props = {
  onProfileGenerated?: () => void;
};

export const useGenerateProduct = ({ onProfileGenerated }: Props) => {
  const { promptNotification } = useNotification();

  const onError = useCallback(
    (error: Error) => {
      promptNotification({ message: error.message, status: 'error' });
    },
    [promptNotification],
  );

  const {
    mutate: mutateProfile,
    isPending: isPendingProfile,
    isSuccess: isSuccessProfile,
  } = useMutation({
    onError,
    onSuccess: (profile) => {
      useProductStore.setState({
        generatedProductDescription: profile.description,
        generatedProductPrice: profile.price,
        generatedProductName: profile.name,
      });
      onProfileGenerated?.();
    },
    mutationFn: async () => {
      return endpoints.generateProductProfile({
        referenceImageUrl: 'https://americanbarbell.com/cdn/shop/products/Mockup_yogamats_v01.jpg',
      });
    },
  });

  const { mutate: mutateImages, isPending: isPendingImages } = useMutation({
    onError,
    onSuccess: ({ urls }) => {
      const productImages = useProductStore.getState().productImages;
      useProductStore.setState({
        productImages: [...productImages, ...urls],
      });
    },
    onSettled: () => {
      useProductStore.setState({ isMediaLoading: false });
    },
    mutationFn: async () => {
      useProductStore.setState({ isMediaLoading: true });
      return endpoints.generateProductImages({
        referenceImageUrl: 'https://americanbarbell.com/cdn/shop/products/Mockup_yogamats_v01.jpg',
      });
    },
  });

  const { mutateAsync: matchCategory, isPending: isPendingMatchCategory } = useMutation({
    onSuccess: ({ category }) => {
      if (Object.values(Category).includes(category as Category)) {
        useAppStore.setState({ category: category as Category });
      }
    },
    mutationFn: (productName: string) => {
      return endpoints.matchProductCategory({
        categories: Object.values(Category),
        productName,
      });
    },
  });

  const startGeneration = useCallback(() => {
    mutateProfile();
    mutateImages();
  }, [mutateProfile, mutateImages]);

  return {
    isPendingMatchCategory,
    isSuccessProfile,
    isPendingProfile,
    startGeneration,
    isPendingImages,
    matchCategory,
  };
};
